import { useCallback, useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/use-auth';
import { useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import PropTypes from "prop-types";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Divider,
  Grid,
  Link,
  Tab,
  Tabs,
  Typography,
  TextField,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AuthGuard from '../../../components/authentication/auth-guard';
import { DashboardLayout } from '../../../components/dashboard/dashboard-layout';
import { CustomerBasicDetails } from '../../../components/dashboard/customer/customer-basic-details';
import { CustomerCourses } from '../../../components/dashboard/customer/customer-courses';
import { CustomerLogs } from '../../../components/dashboard/customer/customer-logs';
import { useMounted } from '../../../hooks/use-mounted';
import * as moment from 'moment';
import { gtm } from '../../../lib/gtm';
import { userApi } from '../../../api/user-api';
import { coursesApi } from '../../../api/course-api';
import { formationsApi } from '../../../api/formation-api';

const tabs = [
  { label: 'Détails', value: 'details' },
  { label: 'Logs', value: 'logs' },
];

const CustomerDetails = (props) => {
  let { customer_id, F_W } = props;
  const { customerId } = useParams();
  const isMounted = useMounted();
  const [customer, setCustomer] = useState(null);
  const [currentTab, setCurrentTab] = useState('details');
  const [open, setOpen] = useState(false);
  const [openSend, setOpenSend] = useState(false);
  const [formations, setFormations] = useState([]);
  const [blockButton, setBlockButton] = useState(false);
  const [disableChoiceSessions, setDisableChoiceSessions] = useState(true);
  const [choiceSession, setChoiceSession] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);

  const [updateFeedbackState, setUpdateFeedbackState] = useState(true);
  const [sessionsList, setSessionsList] = useState([]);
  const [permissions, setPermissions] = useState([]); // État pour stocker les permissions

  const location = useLocation();

  if (!location.state) {
    location.state = {
      "from": "customers",
      "data_from": []
    }
  }
  const { from, data_from } = location.state;
  const { user } = useAuth();

  // Fonction pour récupérer les permissions (avec cache 5 min)
  const getPermissionsByRole = async (roles_id) => {
    const tenant = globalThis.localStorage.getItem('tenant');
    const cacheKey = `permissions_${tenant}_${roles_id}`;
    const cached = JSON.parse(globalThis.localStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      setPermissions(cached.permissions);
      return;
    }
    const url = `${process.env.REACT_APP_BACK}/subdomain/${tenant}/api/permission-verification/get/`;
    const accessToken = globalThis.localStorage.getItem(`accessToken_${tenant}`);
    try {
      const resp = await fetch(url, {
        method: "POST",
        body: JSON.stringify({ roles_id }),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      });
      if (!resp.ok) throw new Error(`HTTP error! Status: ${resp.status}`);
      const json = await resp.json();
      globalThis.localStorage.setItem(cacheKey, JSON.stringify({ permissions: json.permissions, timestamp: Date.now() }));
      setPermissions(json.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  // Fonction pour vérifier si l'utilisateur a la permission "Voir" pour le composant "Cours"
  const hasViewPermission = () => {
    return permissions.some(
      (permission) =>
        permission.component === "Cours" && permission.permission === "Voir"
    );
  };

  // Fonction pour vérifier si l'utilisateur a la permission "create" pour le composant "Cours"
  const hasCreatePermission = () => {
    return permissions.some(
      (permission) =>
        permission.component === "Cours" && permission.permission === "Créer"
    );
  };

  // Récupérer les permissions lorsque le composant est monté
  useEffect(() => {
    if (user && user.roles_id) {
      getPermissionsByRole(user.roles_id);
    }
  }, [user]);

  useEffect(() => {
    gtm.push({ event: 'page_view' });
  }, []);

  const getCustomer = useCallback(async () => {
    try {
      const data = await userApi.getUsersStats(user.admin_id, customer_id ? customer_id : customerId);
      if (isMounted()) {
        setCustomer(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted, customerId, customer_id]);

  const getFormations = useCallback(async () => {
    try {
      const data = await formationsApi.getAllFormations(user.admin_id)
      if (isMounted()) {
        setFormations(Array.isArray(data) ? data : [])
      }
    } catch (err) {
      console.error(err)
    }
  }, [isMounted])

  useEffect(() => {
    Promise.all([getFormations(), getCustomer()]);
    setUpdateFeedbackState(false);
  }, [customerId, customer_id]);

  async function handleChangeFormation(formation_id) {
    setLoadingSessions(true);
    setChoiceSession(null);
    setSessionsList([]);
    setSessionKey(k => k + 1);
    try {
      // Récupérer les sessions liées à cette action (formation)
      const data = await formationsApi.getSessionsByFormationsId(user.admin_id, formation_id);
      // Réponse : { sessions: [...] }
      const sessions = data?.sessions ?? [];
      setSessionsList(Array.isArray(sessions) ? sessions : []);
    } catch (err) {
      console.error(err);
      setSessionsList([]);
    } finally {
      setLoadingSessions(false);
    }
  }

  async function sendIds() {
    await userApi.sendIds(user.admin_id, customer.details.id)
    toast.success('Identifiants de connexion envoyés');
    setBlockButton(true)
    setOpenSend(false)
  }

  const handleSendOpen = () => {
    setOpenSend(true)
  }

  const handleSendClose = () => {
    setOpenSend(false)
  }

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  async function handleAdd() {
    await coursesApi.createCourse(user.admin_id, choiceSession.id, customer_id ? customer_id : customerId)
    setSessionsList([])
    setChoiceSession(null)
    setDisableChoiceSessions(true)
    setSessionKey(k => k + 1)
    handleClose()
    getCustomer()
    toast.success('Apprenant ajouté à une formation');
  }

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          py: 8,
          pt: F_W ? 4 : 8,
          maxWidth: F_W ? "100vw" : "60vw",
        }}
      >
        <Container sx={{
          pt: 8,
          width: "100%",
          minWidth: "100%",
        }}>
          <div>
            <Box sx={{ mb: 4 }}>
              <Link
                component={RouterLink}
                to={from === "" ? `/${globalThis.localStorage.getItem('tenant')}/dashboard/` : `/${globalThis.localStorage.getItem('tenant')}/dashboard/customers`}
                state={{
                  data_from: {
                    auditId: data_from.auditId,
                    startSession: data_from.startSession,
                    endSession: data_from.endSession,
                    sessions_id: data_from.sessions_id,
                    from: data_from.from
                  }
                }}
                color="textPrimary"
                sx={{ alignItems: 'center', display: 'flex', textDecoration: 'none' }}
              >
                <ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  {from === "customers" ? "Liste des apprenants" : from === "" ? "Tableau de bord" : "Retour à la session"}
                </Typography>
              </Link>
            </Box>
            {customer ?
              <>
                {/* ── Profile Header Card ── */}
                <Card
                  sx={{
                    borderRadius: 2.5,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
                    mb: 3,
                    overflow: 'hidden',
                  }}
                >
                  {/* Bande colorée en haut */}
                  <Box sx={{ height: 6, background: 'linear-gradient(90deg, #6060B8 0%, #8282CA 100%)' }} />

                  <Box
                    sx={{
                      px: 3,
                      py: 2.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    {/* Gauche : Avatar + Infos */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                      <Avatar
                        sx={{
                          width: 72,
                          height: 72,
                          backgroundColor: '#6060B8',
                          fontSize: '1.9rem',
                          fontWeight: 700,
                          boxShadow: '0 4px 16px rgba(96,96,184,0.35)',
                          flexShrink: 0,
                        }}
                      >
                        {customer.details.firstname?.charAt(0)?.toUpperCase() || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {customer.details.firstname} {customer.details.lastname}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                          {customer.details.email}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.9, flexWrap: 'wrap' }}>
                          <Chip
                            label={`ID : ${customer.details.id}`}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              backgroundColor: 'rgba(96,96,184,0.1)',
                              color: '#6060B8',
                              fontWeight: 600,
                            }}
                          />
                          {customer.details.last_activity_date && (
                            <Chip
                              icon={<AccessTimeOutlinedIcon sx={{ fontSize: '13px !important' }} />}
                              label={customer.details.last_activity_date}
                              size="small"
                              variant="outlined"
                              color="success"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Droite : Boutons d'action */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignSelf: 'center' }}>
                      {hasCreatePermission() && (
                        <Button
                          variant="contained"
                          onClick={handleClickOpen}
                          startIcon={<PersonAddOutlinedIcon />}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                          Ajouter dans une formation
                        </Button>
                      )}
                      <Button
                        onClick={customer.details.is_mail_sent_credentials === 1 ? handleSendOpen : sendIds}
                        variant="outlined"
                        disabled={blockButton}
                        startIcon={<SendOutlinedIcon />}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                      >
                        Envoyer les identifiants
                      </Button>
                    </Box>
                  </Box>
                </Card>

                {/* ── Dialogs (hors flow visuel) ── */}
                <Box>
                  <Dialog
                    onClose={handleClose}
                    open={open}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogTitle sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonAddOutlinedIcon sx={{ color: '#6060B8' }} />
                        <Typography variant="h6" fontWeight={700}>Ajouter dans une formation</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.25, display: 'block' }}>
                        Sélectionnez d'abord une action, puis choisissez une session
                      </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 2.5, pb: 1 }}>
                      {/* Étape 1 — Action */}
                      <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>
                        Étape 1 — Choisir une action
                      </Typography>
                      <Autocomplete
                        options={formations}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, value) => {
                          if (value) {
                            setDisableChoiceSessions(false);
                            handleChangeFormation(value.id);
                          } else {
                            setDisableChoiceSessions(true);
                            setSessionsList([]);
                            setChoiceSession(null);
                            setSessionKey(k => k + 1);
                          }
                        }}
                        noOptionsText="Aucune action disponible"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Choisir une action"
                            fullWidth
                            sx={{ mt: 1, mb: 2.5 }}
                          />
                        )}
                      />

                      {/* Étape 2 — Session */}
                      <Typography variant="caption" sx={{ color: disableChoiceSessions ? 'text.disabled' : 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>
                        Étape 2 — Choisir une session
                      </Typography>
                      <Autocomplete
                        key={sessionKey}
                        disabled={disableChoiceSessions || loadingSessions}
                        loading={loadingSessions}
                        options={sessionsList}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          if (option.label) return option.label;
                          const num = option.session_number ?? '';
                          const startStr = option.start_date ? moment(option.start_date).format('DD/MM/YYYY') : '';
                          const endStr = option.end_date ? moment(option.end_date).format('DD/MM/YYYY') : '';
                          return `Session N°${num}${startStr ? ` (${startStr}` : ''}${endStr ? ` – ${endStr})` : ''}`;
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, value) => setChoiceSession(value || null)}
                        noOptionsText={loadingSessions ? 'Chargement...' : 'Aucune session disponible pour cette action'}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Choisir une session"
                            fullWidth
                            sx={{ mt: 1 }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingSessions && <CircularProgress size={18} sx={{ mr: 1 }} />}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </DialogContent>
                    <Divider sx={{ mt: 2 }} />
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                      <Button variant="outlined" onClick={handleClose}>Annuler</Button>
                      <Button
                        variant="contained"
                        onClick={handleAdd}
                        disabled={!choiceSession}
                        sx={{ backgroundColor: '#6060B8', '&:hover': { backgroundColor: '#4a4a9a' } }}
                      >
                        Enregistrer
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <Dialog
                    onClose={handleSendClose}
                    open={openSend}
                  >
                    <DialogContent>
                      <DialogContentText>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Attention, vous allez réinitialiser le mot de passe de l&apos;utilisateur. Êtes-vous sûr ?
                        </Typography>
                      </DialogContentText>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                        <Button variant="outlined" onClick={handleSendClose}>Annuler</Button>
                        <Button variant="contained" onClick={sendIds}>Confirmer</Button>
                      </Box>
                    </DialogContent>
                  </Dialog>
                </Box>
                <Tabs
                  indicatorColor="primary"
                  onChange={handleTabsChange}
                  scrollButtons="auto"
                  sx={{ mt: 3 }}
                  textColor="primary"
                  value={currentTab}
                  variant="scrollable"
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                    />
                  ))}
                </Tabs>
              </>
              : //ELSE
              <Container
                style={{ minWidth: '40vw', justifyContent: 'center', alignItems: 'center', height: "80vh", maxWidth: "40vw" }}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress
                    color="inherit"
                    size={100}
                  />
                </Box></Container>
            }
          </div>
          {customer ?
            <>
              <Divider />
              <Box sx={{ mt: 3 }}>
                {currentTab === 'details' && (
                  <Grid
                    container
                    spacing={12}
                  >
                    <Grid
                      item
                      xs={12}
                    >
                      <CustomerBasicDetails
                        customer={customer}
                        setCustomer={setCustomer}
                      />
                    </Grid>
                    {/* Afficher CustomerCourses uniquement si l'utilisateur a la permission "Voir" pour "Cours" */}
                    {hasViewPermission() && (
                      <Grid
                        item
                        xs={12}
                      >
                        <CustomerCourses
                          customer={customer}
                          setCustomer={setCustomer}
                          customerId={customer_id?customer_id:customerId}
                          setUpdateFeedbackState={setUpdateFeedbackState}
                          getCustomer={getCustomer}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
                {currentTab === 'logs' && <CustomerLogs
                  admin_id={user.admin_id}
                  customerid={customer_id ? customer_id : customerId} />}
              </Box>
            </>
            : ""
          }
        </Container>
      </Box>
    </>
  );
};

CustomerDetails.getLayout = (page) => (
  <AuthGuard>
    <DashboardLayout>
      {page}
    </DashboardLayout>
  </AuthGuard>
);

export default CustomerDetails;
CustomerDetails.propTypes = {
  customerId: PropTypes.number,
  F_W: PropTypes.bool,
};